import {useCallback, useMemo} from 'react';
import {SUMMARY_PAYMENTS_STATUSES, toOrderHistoryGroups} from '@chekin/core';
import {
  formatMoney,
  splitCart,
  useBuildTotalPayments,
  useOrderHistory,
  usePaymentSettings,
  usePaymentsCart,
} from '../payments';
import {useHomeEnv} from './env';

type StepCopy = {
  key: string;
  values?: Record<string, string | number>;
};

export type ChecklistStep = {
  id: 'guest_details' | 'payments';
  title: string;
  testId: string;
  completed: boolean;
  path: string;
  subtitle: StepCopy;
  cta: StepCopy;
};

type PaymentsCopyArgs = {
  paymentsComplete: boolean;
  paidAmount: number;
  amountToPay: number;
  holdAmount: number;
  paidPaymentsCount: number;
  totalPayments: number;
  money: (value: number) => string;
};

function buildPaymentsSubtitle({
  paymentsComplete,
  paidAmount,
  amountToPay,
  holdAmount,
  paidPaymentsCount,
  totalPayments,
  money,
}: PaymentsCopyArgs): StepCopy {
  if (paymentsComplete && paidAmount > 0) {
    return {key: 'amount_paid', values: {amount: money(paidAmount)}};
  }
  if (!paymentsComplete && amountToPay > 0 && holdAmount > 0) {
    return {
      key: 'checklist_pay_and_hold',
      values: {pay: money(amountToPay), hold: money(holdAmount)},
    };
  }
  if (!paymentsComplete && amountToPay > 0) {
    return {key: 'amount_left_to_pay', values: {amount: money(amountToPay)}};
  }
  if (!paymentsComplete && holdAmount > 0) {
    return {key: 'checklist_card_hold', values: {amount: money(holdAmount)}};
  }
  if (totalPayments > 0) {
    return {
      key: 'n_of_m_payments_settled',
      values: {done: paidPaymentsCount, count: totalPayments},
    };
  }
  return {key: 'checklist_desc.payments'};
}

function buildPaymentsCta({
  amountToPay,
  holdAmount,
  money,
}: Pick<PaymentsCopyArgs, 'amountToPay' | 'holdAmount' | 'money'>): StepCopy {
  if (amountToPay > 0) {
    return {key: 'checklist_cta_pay', values: {amount: money(amountToPay)}};
  }
  if (holdAmount > 0) {
    return {key: 'checklist_cta_hold', values: {amount: money(holdAmount)}};
  }
  return {key: 'checklist_cta_continue'};
}

export const PAYMENT_TITLE = {
  taxes: 'checklist_title_tourist_tax',
  property_protection: 'checklist_title_deposit',
  booking_payments: 'checklist_title_payment',
} as const;

export function useChecklistSteps() {
  const {
    summary: data,
    isSummaryLoaded: isSuccess,
    guestStats,
    isIVEnabled,
    navigation,
  } = useHomeEnv();
  const {totalGuests, totalRegisteredGuests, totalFullyRegisteredGuests} = guestStats;
  const isLoading = !isSuccess;

  const relevantPaymentStatuses = [
    data?.taxes?.status,
    data?.property_protection?.status,
    data?.booking_payments?.status,
  ].filter(status => status && status !== SUMMARY_PAYMENTS_STATUSES.inactive);
  const paidPaymentsCount = relevantPaymentStatuses.filter(
    status => status === SUMMARY_PAYMENTS_STATUSES.complete,
  ).length;
  const paymentsComplete =
    relevantPaymentStatuses.length > 0 &&
    paidPaymentsCount === relevantPaymentStatuses.length;

  const {currencySymbol} = usePaymentSettings();
  const {paymentsCart} = usePaymentsCart({refetchOnMount: false});
  const {totalToPayNow} = useBuildTotalPayments({paymentsCart});
  const {orderHistory} = useOrderHistory({enabled: paymentsComplete});

  const paidAmount = toOrderHistoryGroups(orderHistory?.payment_details).reduce(
    (sum, entry) => sum + (parseFloat(entry.total_amount_with_commission) || 0),
    0,
  );
  const amountToPay = parseFloat(String(totalToPayNow)) || 0;
  const {holdTotal, preAuthTotal} = splitCart(paymentsCart);
  const holdAmount = holdTotal + preAuthTotal;
  const money = useCallback(
    (value: number) => formatMoney(value, currencySymbol),
    [currencySymbol],
  );

  const steps = useMemo<ChecklistStep[]>(() => {
    if (!isSuccess || !data) return [];

    const guestDetailsDone = totalGuests > 0 && totalRegisteredGuests >= totalGuests;
    const idVerificationDone =
      totalGuests > 0 && totalFullyRegisteredGuests >= totalGuests;

    const activePayments = (
      Object.keys(PAYMENT_TITLE) as Array<keyof typeof PAYMENT_TITLE>
    ).filter(kind => data[kind]?.active);
    const paymentsActive = activePayments.length > 0;
    const paymentsTitle =
      activePayments.length === 1 ? PAYMENT_TITLE[activePayments[0]] : 'payments';

    const registrationDone = isIVEnabled
      ? guestDetailsDone && idVerificationDone
      : guestDetailsDone;

    const all: Array<ChecklistStep & {show: boolean}> = [
      {
        id: 'guest_details',
        title: 'guest_details',
        testId: 'checklist-guest-details',
        completed: registrationDone,
        path: navigation.registration,
        subtitle:
          totalGuests > 0
            ? {
                key: isIVEnabled ? 'n_of_m_guests_verified' : 'n_of_m_guests_registered',
                values: {
                  done: isIVEnabled ? totalFullyRegisteredGuests : totalRegisteredGuests,
                  count: totalGuests,
                },
              }
            : {key: 'checklist_desc.guest_details'},
        cta: {
          key:
            totalRegisteredGuests > 0 ? 'checklist_cta_continue' : 'checklist_cta_start',
        },
        show: true,
      },
      {
        id: 'payments',
        title: paymentsTitle,
        testId: 'checklist-payments',
        completed: paymentsComplete,
        path: navigation.payments,
        subtitle: buildPaymentsSubtitle({
          paymentsComplete,
          paidAmount,
          amountToPay,
          holdAmount,
          paidPaymentsCount,
          totalPayments: relevantPaymentStatuses.length,
          money,
        }),
        cta: buildPaymentsCta({amountToPay, holdAmount, money}),
        show: paymentsActive,
      },
    ];

    return all.filter(step => step.show).map(({show: _show, ...step}) => step);
  }, [
    paymentsComplete,
    paidPaymentsCount,
    relevantPaymentStatuses.length,
    paidAmount,
    amountToPay,
    holdAmount,
    money,
    data,
    isSuccess,
    totalGuests,
    totalRegisteredGuests,
    totalFullyRegisteredGuests,
    isIVEnabled,
    navigation.registration,
    navigation.payments,
  ]);

  return {steps, isLoading};
}
